import type { CodePreviewFile, CodePreviewTreeItem } from '@/services/codePreview';
import { create } from 'zustand';

export interface CodePreviewOpenTab {
  path: string;
  name: string;
}

interface CodePreviewProjectCache {
  treeMap: Record<string, CodePreviewTreeItem[]>;
  expandedPaths: string[];
  query: string;
  openTabs: CodePreviewOpenTab[];
  activePath: string | null;
  fileMap: Record<string, CodePreviewFile>;
  dirty: boolean;
}

interface CodePreviewStore {
  projects: Record<string, CodePreviewProjectCache>;
  getProjectCache: (projectId: string) => CodePreviewProjectCache;
  setTreeItems: (projectId: string, path: string, items: CodePreviewTreeItem[]) => void;
  setExpandedPaths: (projectId: string, expandedPaths: string[]) => void;
  setQuery: (projectId: string, query: string) => void;
  setOpenTabs: (projectId: string, openTabs: CodePreviewOpenTab[]) => void;
  setActivePath: (projectId: string, activePath: string | null) => void;
  setFile: (projectId: string, file: CodePreviewFile) => void;
  markDirty: (projectId: string) => void;
  clearDirty: (projectId: string) => void;
  reset: () => void;
}

export function getCodePreviewCacheKey(projectId: string, commitSha?: string | null): string {
  return `${projectId}:${commitSha || 'latest'}`;
}

function createProjectCache(): CodePreviewProjectCache {
  return {
    treeMap: {},
    expandedPaths: [''],
    query: '',
    openTabs: [],
    activePath: null,
    fileMap: {},
    dirty: false,
  };
}

function ensureProjectCache(
  projects: Record<string, CodePreviewProjectCache>,
  projectId: string,
): Record<string, CodePreviewProjectCache> {
  if (projects[projectId]) {
    return projects;
  }
  return { ...projects, [projectId]: createProjectCache() };
}

export const useCodePreviewStore = create<CodePreviewStore>((set, get) => ({
  projects: {},

  getProjectCache: (projectId) => {
    const cache = get().projects[projectId];
    if (cache) {
      return cache;
    }
    return createProjectCache();
  },

  setTreeItems: (projectId, path, items) =>
    set(state => {
      const projects = ensureProjectCache(state.projects, projectId);
      const cache = projects[projectId];
      return {
        projects: {
          ...projects,
          [projectId]: {
            ...cache,
            treeMap: {
              ...cache.treeMap,
              [path]: items,
            },
          },
        },
      };
    }),

  setExpandedPaths: (projectId, expandedPaths) =>
    set(state => {
      const projects = ensureProjectCache(state.projects, projectId);
      const cache = projects[projectId];
      return {
        projects: {
          ...projects,
          [projectId]: {
            ...cache,
            expandedPaths,
          },
        },
      };
    }),

  setQuery: (projectId, query) =>
    set(state => {
      const projects = ensureProjectCache(state.projects, projectId);
      const cache = projects[projectId];
      return {
        projects: {
          ...projects,
          [projectId]: {
            ...cache,
            query,
          },
        },
      };
    }),

  setOpenTabs: (projectId, openTabs) =>
    set(state => {
      const projects = ensureProjectCache(state.projects, projectId);
      const cache = projects[projectId];
      return {
        projects: {
          ...projects,
          [projectId]: {
            ...cache,
            openTabs,
          },
        },
      };
    }),

  setActivePath: (projectId, activePath) =>
    set(state => {
      const projects = ensureProjectCache(state.projects, projectId);
      const cache = projects[projectId];
      return {
        projects: {
          ...projects,
          [projectId]: {
            ...cache,
            activePath,
          },
        },
      };
    }),

  setFile: (projectId, file) =>
    set(state => {
      const projects = ensureProjectCache(state.projects, projectId);
      const cache = projects[projectId];
      return {
        projects: {
          ...projects,
          [projectId]: {
            ...cache,
            fileMap: {
              ...cache.fileMap,
              [file.path]: file,
            },
          },
        },
      };
    }),

  markDirty: projectId =>
    set(state => {
      const cacheKey = getCodePreviewCacheKey(projectId);
      const projects = ensureProjectCache(state.projects, cacheKey);
      const cache = projects[cacheKey];
      return {
        projects: {
          ...projects,
          [cacheKey]: {
            ...cache,
            dirty: true,
          },
        },
      };
    }),

  clearDirty: projectId =>
    set(state => {
      const projects = ensureProjectCache(state.projects, projectId);
      const cache = projects[projectId];
      return {
        projects: {
          ...projects,
          [projectId]: {
            ...cache,
            dirty: false,
          },
        },
      };
    }),

  reset: () => set({ projects: {} }),
}));
